/**
 * Bundle 79 - Admin call queue.
 *
 * URL: /admin/introductions/calls
 *
 * Lists every introduction currently in `admin_call_required` or a
 * downstream call status. Contacts appear masked; the "Reveal to call"
 * action requires an explicit admin click (audit-logged) and returns
 * the plain phone / email so click-to-call works via a `tel:` link.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PhoneCall, RefreshCw, ShieldAlert, ChevronDown } from 'lucide-react';

const OUTCOMES = [
  'Interested', 'Not_Interested', 'Would_Like_More_Info',
  'Call_Back_Later', 'No_Answer', 'Wrong_Person',
  'Could_Not_Identify', 'Safety_Concern',
];

const AdminCallQueuePage = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mine, setMine] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mine ? { mine: 1 } : {};
      const { data } = await api.get('/admin/introductions/calls', { params });
      setQueue(data?.queue || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load call queue'); }
    finally { setLoading(false); }
  }, [mine]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-call-queue-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Bundle 79 * Phone-first curation</div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Call queue</h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-2xl">
            Claim a task before dialing. Contacts are hidden until you click Reveal, and every
            reveal is audit-logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={mine ? 'default' : 'outline'} onClick={() => setMine((v) => !v)} data-testid="call-queue-mine-toggle">
            {mine ? 'Mine only' : 'All'}
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="call-queue-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {queue.length === 0 ? (
          <Card><CardContent className="p-6 text-neutral-500 text-sm text-center">No open call tasks.</CardContent></Card>
        ) : null}
        {queue.map((row) => (
          <CallRow key={row.introduction.id} row={row} onChanged={load} />
        ))}
      </div>
    </div>
  );
};


const CallRow = ({ row, onChanged }) => {
  const intro = row.introduction;
  const call = row.call || {};
  const [outcome, setOutcome] = useState('Interested');
  const [privateNote, setPrivateNote] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [script, setScript] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [extendReason, setExtendReason] = useState('');
  const [busy, setBusy] = useState(false);

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/introductions/${intro.id}/call/claim`);
      toast.success('Claimed.');
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Claim failed'); }
    finally { setBusy(false); }
  };

  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/introductions/${intro.id}/call/reveal`);
      setRevealed(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reveal failed'); }
    finally { setBusy(false); }
  };

  const loadScript = async () => {
    try {
      const { data } = await api.get(`/admin/introductions/${intro.id}/call/script`);
      setScript(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load script'); }
  };

  const record = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/introductions/${intro.id}/call/record`, {
        outcome, private_note: privateNote,
      });
      toast.success(`Outcome recorded: ${outcome}`);
      if (data?.dispatched_confirmations) toast.info('Confirmation emails dispatched to both parties.');
      await onChanged();
      setPrivateNote('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Record failed'); }
    finally { setBusy(false); }
  };

  const extend = async () => {
    if (extendReason.trim().length < 8) { toast.error('Reason must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/introductions/${intro.id}/call/extend-attempts`, { reason: extendReason });
      toast.success('Attempts extended.');
      setExtendReason('');
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Extend failed'); }
    finally { setBusy(false); }
  };

  const attempts = (call.attempts || []).length;

  return (
    <Card data-testid={`call-row-${intro.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="capitalize">{intro.status.replace(/_/g, ' ')}</Badge>
            <span className="text-xs text-neutral-500 font-mono">{intro.event_slug}</span>
            <span className="text-xs text-neutral-400">{new Date(intro.created_at).toLocaleDateString()}</span>
            <Badge variant="outline">Attempts: {attempts}/{call.max_attempts || 2}</Badge>
            {call.assigned_admin_id ? (
              <Badge variant="outline" className="bg-secondary-50 text-secondary-800 border-secondary-200" data-testid={`call-assignee-${intro.id}`}>
                Claimed by {call.assigned_admin_id.slice(0, 8)}
              </Badge>
            ) : null}
          </div>
          {intro.status === 'safety_hold' ? (
            <div className="text-xs text-red-700 inline-flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Safety hold</div>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Requester</div>
            <div className="text-primary-900 font-semibold">{intro.reporter?.first_name}</div>
            <div className="text-neutral-600 text-xs">{intro.reporter?.email}</div>
            <div className="text-emerald-700 text-[11px] mt-0.5">Consent shown by admin: yes</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Possible recipient</div>
            {revealed ? (
              <div data-testid={`call-revealed-${intro.id}`}>
                {revealed.phone ? (
                  <a href={`tel:${revealed.phone}`} className="text-secondary-800 underline font-mono" data-testid={`call-tel-link-${intro.id}`}>
                    <PhoneCall className="h-3.5 w-3.5 inline mr-1" /> {revealed.phone}
                  </a>
                ) : <div className="text-neutral-500 text-xs">No phone on file.</div>}
                <div className="text-neutral-600 text-xs">{revealed.email}</div>
              </div>
            ) : (
              <div>
                <div className="text-primary-900 font-mono">{row.target_masked?.phone_masked || '***'}</div>
                <div className="text-neutral-500 text-xs">{row.target_masked?.email_masked || '***'}</div>
                <Button size="sm" variant="outline" onClick={reveal} className="mt-1" data-testid={`call-reveal-btn-${intro.id}`}>
                  Reveal to call
                </Button>
              </div>
            )}
            <div className="text-[11px] mt-1 text-neutral-500">
              allow_introductions at dispatch: {String(!!row.target_masked?.recipient_toggle_at_dispatch)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">What they noticed</div>
            <div className="text-primary-900 text-xs whitespace-pre-wrap">{intro.target_hint?.description}</div>
          </div>
        </div>

        {!call.assigned_admin_id ? (
          <Button size="sm" onClick={claim} disabled={busy} className="bg-primary-900 text-white hover:bg-primary-800" data-testid={`call-claim-btn-${intro.id}`}>
            Claim call
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => { setExpanded((v) => !v); if (!script) loadScript(); }} data-testid={`call-expand-btn-${intro.id}`}>
              <ChevronDown className={`h-3.5 w-3.5 mr-1 transition-transform ${expanded ? 'rotate-180' : ''}`} /> Record outcome
            </Button>
            {expanded ? (
              <div className="border border-neutral-200 rounded-md p-3 space-y-3 bg-neutral-50">
                {script ? (
                  <div className="text-xs text-neutral-700 bg-white rounded-md p-2 border border-neutral-200" data-testid={`call-script-${intro.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-secondary-800 mb-1">Suggested script</div>
                    {script.script}
                  </div>
                ) : null}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-neutral-500">Outcome</label>
                  <select
                    value={outcome} onChange={(e) => setOutcome(e.target.value)}
                    className="border border-neutral-200 rounded-md px-2 py-1 text-sm"
                    data-testid={`call-outcome-select-${intro.id}`}
                  >
                    {OUTCOMES.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <Textarea
                  value={privateNote} onChange={(e) => setPrivateNote(e.target.value)}
                  placeholder="Private notes on the call (admin only)"
                  className="min-h-[70px]"
                  data-testid={`call-private-note-${intro.id}`}
                />
                <Button size="sm" onClick={record} disabled={busy} className="bg-primary-900 text-white hover:bg-primary-800" data-testid={`call-record-btn-${intro.id}`}>
                  Record outcome
                </Button>
                {attempts >= (call.max_attempts || 2) ? (
                  <div className="border-t border-neutral-200 pt-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Reached max attempts</div>
                    <Input
                      value={extendReason} onChange={(e) => setExtendReason(e.target.value)}
                      placeholder="Override reason (audit-logged, min 8 chars)"
                      data-testid={`call-extend-reason-${intro.id}`}
                    />
                    <Button size="sm" variant="outline" onClick={extend} disabled={busy} className="mt-2" data-testid={`call-extend-btn-${intro.id}`}>
                      Extend attempts
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCallQueuePage;
